// src/modules/app-views/services/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import { Tabs } from 'antd';
import translateX from 'utils/translateX';
import { useEffect, useMemo } from 'react';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { buildInitialValues } from 'utils/mock';
import useFormInstance from 'hooks/useFormInstance';
import { FormOutlined, AuditOutlined, SettingOutlined, DollarOutlined } from '@ant-design/icons';
import { RiRobot2Line } from 'react-icons/ri';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import ServicesRecordTab from 'modules/app-views/services/pages/Document/tabs/RecordTab';
import ServicesFiscalTab from 'modules/app-views/services/pages/Document/tabs/FiscalTab';
import ServicesParametersTab from 'modules/app-views/services/pages/Document/tabs/ParametersTab';
import ServicesCostsTab from 'modules/app-views/services/pages/Document/tabs/CostsTab';
import ServicesAiInfoTab from 'modules/app-views/services/pages/Document/tabs/AiInfoTab';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMockStep = false;
const TAB_LABEL_STYLE = { display: 'flex', alignItems: 'center', gap: 6 };
const STYLES = {
  cardHeader: { position: 'relative' },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ServicesDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode, registerTabFieldPrefixes } =
    useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  // Get upsert from global state - auto-fetch is handled by DocumentProvider
  const { upsert, urlInfo } = useZustand('global', 'useApi');
  const { formRef, form, isInitializedRef } = useFormInstance({ data });
  const _id = urlInfo?.params?._id;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const requiredFields = ['name'];

  const tabsItems = useMemo(() => {
    const src = Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
    return src;
  }, [availableTabs]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // NOTE: useMemo instead of useState to react when data becomes available
  const initialValues = useMemo(() => buildInitialValues(isMockStep, data), [data]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!initTab) return;
    registerTabFieldPrefixes?.({ '': initTab });
  }, [registerTabFieldPrefixes, initTab]);

  // Build tabs
  useEffect(() => {
    const childProps = {
      ...props,
      initTab,
      viewMode,
      data,
    };
    const itemsLocal = buildTabsArray(childProps);
    buildTabs(itemsLocal);
  }, [initTab, viewMode, data]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ values, allValues }) => {
    try {
      const serviceId = values?._id || allValues?._id || data?._id;
      const dataToSave = { ...values };

      await upsert({ dataSet: dataToSave, _id: serviceId });
    } catch (err) {
      throw err;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus="name"
          reloadEntirePage={true}
          ignoreResetFormOnSubmit={true}
          requiredFields={requiredFields}
          locked={viewMode === 'view'}
          showInfoMessage={true}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div style={STYLES.cardHeader}>
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              avatarName={data?.name}
              watchField="countryCode"
            />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ServicesDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, viewMode, data } = props;

  const isNewDocument = viewMode === 'add';
  const hasCatalogedByAi = data?.aiCatalogMeta?.catalogedByAI === true;

  return [
    {
      key: initTab,
      label: (
        <span style={TAB_LABEL_STYLE}>
          <FormOutlined />
          {translateX('record')}
        </span>
      ),
      hide: false,
      disabled: false,
      children: <ServicesRecordTab {...props} tabName={initTab} />,
    },
    {
      key: 'fiscal',
      label: (
        <span style={TAB_LABEL_STYLE}>
          <AuditOutlined />
          {translateX('fiscal')}
        </span>
      ),
      hide: false,
      disabled: isNewDocument,
      children: <ServicesFiscalTab {...props} tabName="fiscal" />,
    },
    {
      key: 'costs',
      label: (
        <span style={TAB_LABEL_STYLE}>
          <DollarOutlined />
          {translateX('costs')}
        </span>
      ),
      hide: false,
      disabled: isNewDocument,
      children: <ServicesCostsTab {...props} tabName="costs" />,
    },
    {
      key: 'aiInfo',
      label: (
        <span style={TAB_LABEL_STYLE}>
          <RiRobot2Line />
          {translateX('ai_cataloging')}
        </span>
      ),
      hide: !hasCatalogedByAi,
      disabled: false,
      children: <ServicesAiInfoTab {...props} tabName="aiInfo" />,
    },
    {
      key: 'parameters',
      label: (
        <span style={TAB_LABEL_STYLE}>
          <SettingOutlined />
          {translateX('parameters')}
        </span>
      ),
      hide: false,
      disabled: isNewDocument,
      children: <ServicesParametersTab {...props} tabName="parameters" />,
    },
  ];
}
